import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  Button,
  IconButton,
  Stack,
} from '@mui/material';
import {
  ChangeEventHandler,
  MouseEventHandler,
  SyntheticEvent,
  UIEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { AutoCompleteOption, DynamicIcon, Tooltip } from 'src/components';
import { CreateEntityAutocomplete } from './create-entity-autocomplete';
import { FilterResultsTextField } from './filter-results-text-field';

export const AutocompleteSearchBar = <T extends AutoCompleteOption>({
  addButtonText,
  onAddButtonClick,
  createButtonText,
  filteredEntities,
  isLoading,
  searchPlaceholder,
  hideSearchBar,
  handleOnFilterResultsChange,
  handleScroll,
  onAutocompleteChange,
  onCreateButtonClick,
  onAutocompleteTextFieldChange,
  disabled = false,
}: {
  addButtonText: string;
  onAddButtonClick?: MouseEventHandler<HTMLButtonElement>;
  createButtonText: string;
  filteredEntities: T[];
  isLoading?: boolean;
  searchPlaceholder: string;
  hideSearchBar?: boolean;
  disabled?: boolean;
  handleOnFilterResultsChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleScroll: (e: UIEvent) => Promise<void>;
  onAutocompleteChange: (
    event: SyntheticEvent,
    value: AutocompleteValue<T, false, false, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
  ) => Promise<void>;
  onCreateButtonClick: MouseEventHandler<HTMLButtonElement> | undefined;
  onAutocompleteTextFieldChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}) => {
  const autocompleteInputRef = useRef<HTMLElement | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isAddEntityOpen, setIsAddEntityOpen] = useState(false);

  const onFilterFieldBlur = useCallback(() => {
    // don't collapse field if it has value
    setIsSearchOpen(!!inputRef.current?.value);
  }, []);

  return (
    <Stack direction="row" alignItems="start" justifyContent="space-between">
      <FilterResultsTextField
        inputRef={inputRef}
        handleOnChange={handleOnFilterResultsChange}
        isTextFieldOpen={isSearchOpen}
        onBlur={onFilterFieldBlur}
      />
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        width={isSearchOpen || isAddEntityOpen ? 0 : '100%'}
        overflow="hidden"
        justifyContent="space-between"
        sx={{
          transition: '0.2s width',
          order: hideSearchBar ? 2 : undefined,
        }}
      >
        {!hideSearchBar && (
          <Tooltip title="Open search bar">
            <IconButton
              onClick={() => {
                setIsSearchOpen((currentValue) => !currentValue);
                inputRef.current?.focus();
              }}
            >
              <DynamicIcon name="search" filled />
            </IconButton>
          </Tooltip>
        )}
        <Button
          disabled={disabled}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            if (onAddButtonClick) {
              onAddButtonClick(event);
            } else {
              setIsAddEntityOpen((currentValue) => !currentValue);
              autocompleteInputRef.current?.focus();
            }
          }}
          sx={{
            width: 'fit-content',
            alignSelf: 'end',
          }}
        >
          {addButtonText}
        </Button>
      </Stack>
      <CreateEntityAutocomplete
        createButtonText={createButtonText}
        inputRef={autocompleteInputRef}
        isAddEntityOpen={isAddEntityOpen}
        setIsAddEntityOpen={setIsAddEntityOpen}
        filteredEntities={filteredEntities}
        searchPlaceholder={searchPlaceholder}
        handleScroll={handleScroll}
        onAutocompleteChange={onAutocompleteChange}
        onCreateButtonClick={onCreateButtonClick}
        onAutocompleteTextFieldChange={onAutocompleteTextFieldChange}
        isLoading={isLoading}
      />
    </Stack>
  );
};
